import { z } from 'zod';
import { budgetReserveSettingsSchema } from './budgetPlanning.js';
const opaqueId = z.string().min(1).max(100);
const revision = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const requiredText = (max) => z
    .string()
    .max(max)
    .refine((value) => value.trim().length > 0, 'Enter text');
export const storedBudgetAmountSchema = z
    .number()
    .nonnegative()
    .int()
    .refine((value) => Number.isFinite(value) && Math.fround(value) === value, 'The budget must store this whole-dollar figure exactly');
export const approvalFinancialLineSchema = z.strictObject({
    taxonomyId: z.number().int().positive(),
    direction: z.enum(['income', 'expense']),
    label: requiredText(300),
    amount: storedBudgetAmountSchema,
});
export const packageWordingSchema = z.strictObject({
    narrative: requiredText(20_000),
    keyDecisions: z.array(requiredText(2000)).min(1).max(100),
});
export const approvalRecipientSchema = z.strictObject({
    memberId: opaqueId,
    accountId: z.number().int().positive(),
    name: requiredText(300),
    email: z.email().max(255),
});
export const approvalAudienceSchema = z
    .array(approvalRecipientSchema)
    .min(1)
    .max(100)
    .refine((members) => new Set(members.map((member) => member.memberId)).size === members.length, 'Select each member once')
    .refine((members) => new Set(members.map((member) => member.accountId)).size === members.length, 'Each selected member needs a distinct account');
export const approvalScenarioSettingSchema = z.strictObject({
    id: opaqueId,
    label: requiredText(100),
    basis: requiredText(2000),
    cpiRate: z.number().min(0).max(100),
    reserve: budgetReserveSettingsSchema,
});
export const approvalScenarioSettingsSchema = z
    .array(approvalScenarioSettingSchema)
    .length(3)
    .refine((scenarios) => new Set(scenarios.map((scenario) => scenario.id)).size === 3, 'Three distinct scenario identities are required');
const identifiedSection = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('available'),
        period: requiredText(100),
        basis: requiredText(2000),
        text: requiredText(2_000_000),
    }),
    z.strictObject({ state: z.enum(['unavailable', 'inapplicable']), reason: requiredText(2000) }),
]);
export const reviewedBudgetPackageSchema = z
    .strictObject({
    formatVersion: z.literal(1),
    ownershipDisplayId: opaqueId,
    ownershipName: requiredText(300),
    planDisplayId: opaqueId,
    fiscalYear: z.number().int().min(2000).max(9999),
    version: z.number().int().positive(),
    sourceRevision: revision,
    propertyKind: z.enum(['association', 'rental']),
    financialFingerprint: opaqueId,
    proposedBudget: z.array(approvalFinancialLineSchema).min(1).max(500),
    history: identifiedSection,
    scenarios: z
        .array(z.strictObject({
        setting: approvalScenarioSettingSchema,
        proposed: z.boolean(),
        lines: z.array(approvalFinancialLineSchema).min(1).max(500),
    }))
        .length(3),
    reserveOutlook: identifiedSection,
    flags: z.array(z.string().min(1).max(2000)).max(100),
    wording: packageWordingSchema,
})
    .superRefine((value, context) => {
    if (value.scenarios.filter((scenario) => scenario.proposed).length !== 1) {
        context.addIssue({
            code: 'custom',
            message: 'Identify exactly one proposed scenario',
            path: ['scenarios'],
        });
    }
    if (new Set(value.scenarios.map((scenario) => scenario.setting.id)).size !== 3) {
        context.addIssue({
            code: 'custom',
            message: 'Scenario identities must be distinct',
            path: ['scenarios'],
        });
    }
    if (value.propertyKind === 'association' && value.reserveOutlook.state === 'inapplicable') {
        context.addIssue({
            code: 'custom',
            message: 'An association needs an identified reserve outlook or explicit unavailable data',
            path: ['reserveOutlook'],
        });
    }
});
export const approvalRequestStateSchema = z.enum([
    'pending',
    'awaiting_prerequisites',
    'finalizing',
    'approved_complete',
    'rejected',
    'withdrawn',
    'rolled_back',
]);
export const approvalResponseCommandSchema = z.strictObject({
    requestId: opaqueId,
    operationId: opaqueId,
    expectedRevision: revision,
    decision: z.enum(['approve', 'reject']),
    comment: z.string().max(5000).optional(),
});
//# sourceMappingURL=budgetApproval.js.map